import { Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { useClient } from '@/contexts/ClientContext';
import { LayoutDashboard, Building2, DollarSign, Settings, LogOut, Package, Users, CreditCard } from 'lucide-react';
import { ClientSwitcher } from './ClientSwitcher';

export function Layout() {
  const { user, logout } = useAuth();
  const { role, isPartner, isClient } = useRole();
  const { clients } = useClient();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Top Navigation */}
      <nav className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo & Client Switcher */}
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                AIIA {isPartner ? 'Partner' : isClient ? 'Client' : ''} Portal
              </h1>
              {isPartner && (
                <>
                  <div className="h-6 w-px bg-slate-700" />
                  <ClientSwitcher />
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-300">{user?.full_name || user?.name || user?.email}</span>
                  {role && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 capitalize">
                      {role}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">{user?.partner?.name || user?.partner_name}</div>
              </div>
              <button
                onClick={logout}
                className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
                title="Logout"
              >
                <LogOut className="h-5 w-5 text-slate-400" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-slate-800/30 border-r border-slate-700 min-h-[calc(100vh-4rem)]">
          <nav className="p-4 space-y-2">
            {/* Partner Navigation */}
            {isPartner && (
              <>
                <Link
                  to="/partner/dashboard"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === '/partner/dashboard' || location.pathname === '/'
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
                  }`}
                >
                  <LayoutDashboard className="h-5 w-5" />
                  <span className="font-medium">Dashboard</span>
                </Link>
                <Link
                  to="/partner/dashboard"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname.startsWith('/partner/clients')
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
                  }`}
                >
                  <Building2 className="h-5 w-5" />
                  <span className="font-medium">My Clients</span>
                </Link>
                <Link
                  to="/partner/billing"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === '/partner/billing'
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
                  }`}
                >
                  <DollarSign className="h-5 w-5" />
                  <span className="font-medium">Billing</span>
                </Link>
              </>
            )}

            {/* Client Navigation */}
            {isClient && (
              <>
                <Link
                  to="/client/dashboard"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === '/client/dashboard' || location.pathname === '/'
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
                  }`}
                >
                  <LayoutDashboard className="h-5 w-5" />
                  <span className="font-medium">Dashboard</span>
                </Link>
                <Link
                  to="/client/instances"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname.startsWith('/client/instances')
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
                  }`}
                >
                  <Package className="h-5 w-5" />
                  <span className="font-medium">My Instances</span>
                </Link>
                <Link
                  to="/client/subscription"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === '/client/subscription'
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
                  }`}
                >
                  <CreditCard className="h-5 w-5" />
                  <span className="font-medium">Subscription</span>
                </Link>
                <Link
                  to="/client/users"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === '/client/users'
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
                  }`}
                >
                  <Users className="h-5 w-5" />
                  <span className="font-medium">Users</span>
                </Link>
              </>
            )}

            {/* Settings (common to all roles) */}
            <Link
              to="/settings"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === '/settings'
                  ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                  : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-300'
              }`}
            >
              <Settings className="h-5 w-5" />
              <span className="font-medium">Settings</span>
            </Link>
          </nav>

          {/* Partner Info */}
          {isPartner && (
            <div className="p-4 mt-8">
              <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
                <div className="text-xs font-medium text-slate-500 mb-2">PARTNER INFO</div>
                <div className="space-y-1">
                  <div className="text-sm text-slate-300">
                    Tier: <span className="font-semibold text-emerald-400">{user?.partner?.tier || user?.partner_tier}</span>
                  </div>
                  <div className="text-sm text-slate-300">
                    Clients: <span className="font-semibold">{clients.length}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
